<div class="chat-thread d-flex flex-column h-100">
  <!-- Header -->
  <div
    class="chat-thread__header border-bottom px-3 py-2 d-flex align-items-center gap-2 flex-shrink-0"
  >
    <span class="avatar-xs rounded-circle bg-soft-info flex-shrink-0">
      <i class="avatar-title text-info font-16 ri-discuss-line"></i>
    </span>
    <div class="flex-grow-1 overflow-hidden">
      <h5 class="m-0 text-truncate">{{ "chat.thread.title" | translate }}</h5>
      <small class="text-muted">{{
        "chat.thread.replyCount" | translate: { count: rows.length }
      }}</small>
    </div>
    <!-- Icon-only, so the tooltip is the affordance text for pointer users and
         the aria-label the one for assistive tech — both have to stay. The glyph
         is the "return" arrow rather than a directional back arrow: the button
         sits at the trailing edge, where a left-pointing arrow would fight its
         own position. -->
    <button
      type="button"
      class="btn btn-sm btn-light flex-shrink-0"
      (click)="close()"
      [matTooltip]="'chat.thread.back' | translate"
      [attr.aria-label]="'chat.thread.back' | translate"
    >
      <i class="ri-arrow-go-back-line" aria-hidden="true"></i>
    </button>
  </div>

  <!-- Root + replies -->
  <div
    #threadScroll
    class="chat-thread__body flex-grow-1 overflow-auto px-3 py-2"
  >
    @if (loadingThread && rows.length === 0 && !rootRow) {
      <div
        class="d-flex flex-column align-items-center justify-content-center gap-2 py-5"
      >
        <div class="spinner-border text-primary" role="status"></div>
        <span class="text-muted">{{ "chat.thread.loading" | translate }}</span>
      </div>
    } @else if (loadError) {
      <div
        class="d-flex flex-column align-items-center justify-content-center gap-2 py-5 px-3 text-center"
      >
        <i class="ri-error-warning-line font-24 text-danger"></i>
        <span class="text-muted">{{
          "chat.thread.loadError" | translate
        }}</span>
        <button
          type="button"
          class="btn btn-sm btn-soft-primary"
          (click)="retryLoad()"
        >
          {{ "chat.bubble.retry" | translate }}
        </button>
      </div>
    } @else {
      <!-- Thread root -->
      @if (rootRow) {
        <div class="chat-thread__root border rounded p-2 mb-2">
          <ng-container
            [ngTemplateOutlet]="threadRow"
            [ngTemplateOutletContext]="{ row: rootRow, isRoot: true }"
          ></ng-container>
        </div>
      }

      @if (hasMore) {
        <div class="text-center mb-2">
          <button
            type="button"
            class="btn btn-sm btn-light"
            [disabled]="loadingOlder"
            (click)="loadOlder()"
          >
            @if (loadingOlder) {
              <span
                class="spinner-border spinner-border-sm me-1"
                role="status"
              ></span>
            }
            {{ "chat.thread.loadOlder" | translate }}
          </button>
        </div>
      }

      @if (rows.length === 0) {
        <div
          class="d-flex flex-column align-items-center justify-content-center gap-2 py-4 px-3 text-center"
        >
          <i class="ri-discuss-line font-24 text-muted"></i>
          <span class="text-muted">{{ "chat.thread.empty" | translate }}</span>
        </div>
      }

      @for (row of rows; track trackRow($index, row)) {
        <ng-container
          [ngTemplateOutlet]="threadRow"
          [ngTemplateOutletContext]="{ row: row, isRoot: false }"
        ></ng-container>
      }
    }
  </div>

  <!-- Reply composer — one THREAD_REPLY per submit (parentMessage set) -->
  <chat-composer
    class="flex-shrink-0"
    [disabled]="disabled || !rootMessageId"
    [members]="members"
    [channelId]="channelId"
    (send)="onComposerSend($event)"
    (typingChange)="onTypingChange($event)"
  ></chat-composer>
</div>

<!-- Shared row renderer (root and replies) -->
<ng-template #threadRow let-row="row" let-isRoot="isRoot">
  <div
    class="chat-thread__row d-flex gap-2 py-1"
    [class.chat-thread__row--pending]="row.message.sendState === 'sending'"
  >
    @if (row.authorImage) {
      <img
        class="avatar-xs rounded-circle flex-shrink-0"
        [src]="row.authorImage"
        [alt]="row.authorName"
      />
    } @else {
      <span class="avatar-xs rounded-circle bg-soft-primary flex-shrink-0">
        <span class="avatar-title text-primary">{{ row.authorInitial }}</span>
      </span>
    }

    <div class="flex-grow-1 overflow-hidden">
      <div class="d-flex align-items-baseline gap-2">
        <span class="fw-semibold chat-name">{{ row.authorName }}</span>
        <small class="text-muted flex-shrink-0">{{ row.timeLabel }}</small>
      </div>

      <!-- Forwarded banner (RF-13) — same rule as the pane rows. -->
      @if (row.forwardedLabel && !row.message.deleted) {
        <small
          class="d-flex align-items-center gap-1 text-muted fst-italic"
        >
          <i class="ri-share-forward-line" aria-hidden="true"></i>
          <span class="text-truncate">{{ row.forwardedLabel }}</span>
        </small>
      }

      @if (row.message.deleted) {
        <p class="m-0 fst-italic text-muted">
          {{ "chat.conversation.deletedMessage" | translate }}
        </p>
      } @else {
        @if (row.message.type === messageType.IMAGE) {
          <!-- Thumbnail — opens the viewer over the thread's images -->
          <button
            type="button"
            class="btn p-0 border-0 d-inline-block mt-1 chat-thread__image-button"
            (click)="openImage(row)"
            [attr.aria-label]="row.message.fileName"
          >
            <img
              class="chat-thread__image rounded border"
              [src]="row.message.fileUrl"
              [alt]="row.message.fileName"
            />
          </button>
        } @else if (row.message.type === messageType.VIDEO) {
          <video
            class="chat-thread__video rounded border mt-1"
            controls
            preload="metadata"
            [src]="row.message.fileUrl"
          ></video>
        } @else if (row.message.type === messageType.AUDIO) {
          <chat-audio-player
            [src]="row.message.fileUrl"
            [messageId]="row.message.clientMessageId || row.message._id"
            [fileName]="row.message.fileName"
          ></chat-audio-player>
        } @else if (row.message.type === messageType.DOCUMENT) {
          <div
            class="chat-thread__file border rounded d-flex align-items-center gap-2 p-2 mt-1"
          >
            <span class="avatar-xs rounded bg-soft-primary flex-shrink-0">
              <i
                class="avatar-title text-primary font-16"
                [ngClass]="row.fileIcon"
              ></i>
            </span>
            <div class="flex-grow-1 overflow-hidden">
              <small class="d-block text-truncate fw-semibold">{{
                row.message.fileName
              }}</small>
              <small class="text-muted d-block">{{ row.fileSizeLabel }}</small>
            </div>
            <a
              class="btn btn-sm btn-light flex-shrink-0"
              [href]="row.message.fileUrl"
              target="_blank"
              rel="noopener noreferrer"
              download
              [matTooltip]="'chat.conversation.download' | translate"
              [attr.aria-label]="'chat.conversation.download' | translate"
            >
              <i class="ri-download-2-line"></i>
            </a>
          </div>
        } @else {
          <p
            class="m-0 chat-thread__content"
            [innerHTML]="row.message.content | chatMarkdown: row.mentionRender"
          ></p>
        }

        <!-- Work reference (T-22/T-24 · RF-35) — snapshot only, deep-linkable -->
        @if (row.message.contextRef) {
          <chat-context-ref-card
            [reference]="row.message.contextRef"
          ></chat-context-ref-card>
        }

        <span class="d-inline-flex align-items-center flex-wrap gap-2">
          @if (row.message.editedAt) {
            <small class="text-muted">{{
              "chat.conversation.edited" | translate
            }}</small>
          }
          @if (row.message.sendState === "sending") {
            <small class="text-muted d-inline-flex align-items-center gap-1">
              <i class="ri-time-line"></i
              >{{ "chat.conversation.sending" | translate }}
            </small>
          } @else if (row.message.sendState === "failed") {
            <small class="text-danger d-inline-flex align-items-center gap-1">
              <i class="ri-error-warning-line"></i
              >{{ "chat.conversation.sendFailed" | translate }}
            </small>
            <button
              type="button"
              class="btn btn-sm btn-soft-primary chat-thread__retry"
              (click)="retrySend(row)"
            >
              <i class="ri-restart-line me-1"></i
              >{{ "chat.bubble.retry" | translate }}
            </button>
            <button
              type="button"
              class="btn btn-sm btn-light chat-thread__retry"
              (click)="discardSend(row)"
            >
              <i class="ri-delete-bin-line me-1"></i
              >{{ "chat.conversation.discard" | translate }}
            </button>
          }
        </span>
      }

      <!-- Reactions (aggregated counters — same command as the main flow) -->
      @if (!row.message.deleted && row.reactions.length > 0) {
        <div class="d-flex align-items-center flex-wrap gap-1 mt-1">
          @for (reaction of row.reactions; track reaction.emoji) {
            <button
              type="button"
              class="btn btn-sm chat-thread__reaction"
              [class.btn-soft-primary]="reaction.mine"
              [class.btn-light]="!reaction.mine"
              (click)="toggleReaction(row, reaction.emoji)"
            >
              <span>{{ reaction.emoji }}</span>
              <small class="ms-1">{{ reaction.count }}</small>
            </button>
          }
        </div>
      }
    </div>
  </div>
  @if (isRoot) {
    <div class="chat-thread__root-divider d-flex align-items-center mt-1">
      <span class="flex-grow-1 border-top"></span>
      <small class="text-muted mx-2">{{
        "chat.thread.repliesDivider" | translate
      }}</small>
      <span class="flex-grow-1 border-top"></span>
    </div>
  }
</ng-template>
